import React, { useState, useEffect, useRef, useMemo } from "react";
import { MdClose, MdFileUpload, MdCheckCircle } from "react-icons/md";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import moment from "moment";
import { searchPlaces, getPlaceDetails } from "../../../../Service/OlaMapsService";
import {
  checkPhoneExists,
  createVisitForPatient,
  getPatientById,
  getPartner,
} from "../../../../Service/partnerAPI";
import { getLookupData } from "../../../../Service/lookup";
import { showToast } from "../../../../reusableUI/toaster";

const inputClass =
  "w-full bg-[#F6F8FC] rounded-xl px-4 py-2.5 text-[13px] font-poppins border border-gray-200 outline-none focus:border-[#3E57E8] focus:ring-2 focus:ring-[#3E57E8]/15 text-gray-700 transition-all";

const reactSelectStyles = {
  control: (base, state) => ({
    ...base,
    backgroundColor: "#F6F8FC",
    borderRadius: "0.75rem",
    minHeight: 44,
    borderColor: state.isFocused ? "#3E57E8" : "#e5e7eb",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(62,87,232,0.15)" : "none",
    ":hover": { borderColor: "#3E57E8" },
    fontSize: 13,
  }),
  valueContainer: (base) => ({ ...base, padding: "2px 12px" }),
  placeholder: (base) => ({ ...base, color: "#9CA3AF", fontSize: 13 }),
  multiValue: (base) => ({ ...base, backgroundColor: "#E7EEFF" }),
  multiValueLabel: (base) => ({ ...base, fontSize: 12, color: "#1F2937" }),
  menu: (base) => ({ ...base, zIndex: 9999 }),
};

const formatServiceOptionWithNotes = (option, { context }) => {
  if (context !== "menu") return option.label;
  const notes = String(option?.notes || "").trim();
  return (
    <div className="flex flex-col">
      <span>{option.label}</span>
      {notes ? (
        <span className="text-[11px] text-gray-500 italic mt-0.5">{notes}</span>
      ) : null}
    </div>
  );
};

const safeArr = (v) => (Array.isArray(v) ? v : []);
const toNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const getDiscountedFee = (rawFee, discount) => {
  const fee = toNumber(rawFee);
  if (fee <= 0 || !discount || typeof discount !== "object") return fee;

  const discountType = String(discount?.type || "").trim().toLowerCase();
  const discountValue = toNumber(discount?.value);
  if (discountValue <= 0) return fee;

  if (discountType === "percentage") {
    const percentage = Math.min(discountValue, 100);
    return Math.max(0, Number((fee - (fee * percentage) / 100).toFixed(2)));
  }

  if (discountType === "fixed") {
    return Math.max(0, Number((fee - discountValue).toFixed(2)));
  }

  return fee;
};
const NAME_REGEX = /^[A-Za-z ]{2,50}$/;
const isValidName = (name) => {
  if (!name) return false;
  return NAME_REGEX.test(String(name).trim());
};
const hasValidCoordinates = (coordinates) => {
  if (!coordinates || typeof coordinates !== "object") return false;
  const lat = Number(coordinates?.lat);
  const lng = Number(coordinates?.lng);
  return Number.isFinite(lat) && Number.isFinite(lng);
};
const getDobBounds = () => {
  const todayMoment = moment().startOf("day");
  return {
    minDobMoment: todayMoment.clone().subtract(100, "years"),
    maxDobMoment: todayMoment,
  };
};
const formatDobBound = (dobMoment) =>
  dobMoment && typeof dobMoment.isValid === "function" && dobMoment.isValid()
    ? dobMoment.format("YYYY-MM-DD")
    : "";
const normalizePartnerServiceToken = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
const extractPartnerServiceString = (service) => {
  if (typeof service === "string") return service.trim();
  if (service && typeof service === "object") {
    return String(
      service?.value ??
      service?.name ??
      service?.label ??
      service?.service ??
      service?.type ??
      ""
    ).trim();
  }
  return "";
};
const mapPartnerServicesToCareCategories = (services = []) => {
  const categories = [];
  const addCategory = (value, label) => {
    if (!categories.some((item) => item.value === value)) {
      categories.push({ value, label });
    }
  };

  safeArr(services).forEach((service) => {
    const normalized = normalizePartnerServiceToken(extractPartnerServiceString(service));
    if (!normalized) return;

    if (
      normalized.includes("nurse") ||
      normalized.includes("nursing assistant") ||
      normalized.includes("nursing-assistant")
    ) {
      addCategory("Nursing Care", "Nursing Care");
    }

    if (
      normalized.includes("physio") ||
      normalized.includes("physiotherapist") ||
      normalized.includes("physiotherapy")
    ) {
      addCategory("Physio", "Physiotherapy");
    }
  });

  return categories;
};

const shiftLabel = (shift) => {
  if (shift === "morning") return "Morning";
  if (shift === "night") return "Night";
  if (shift === "24/7") return "24/7";
  return shift || "";
};

const dutyKey = (shift, type) => `${shift}__${type}`;
const MAX_PDF_SIZE_BYTES = 10 * 1024 * 1024;
const getDefaultProfile = () => ({
  abhaNumber: "",
  mrn: "",
  title: "Mr",
  name: "",
  dob: "",
  gender: "male",
  phoneCode: "91",
  phoneNumber: "",
  emergencyCode: "91",
  emergencyNumber: "",
  flatNo: "",
  address: "",
  city: "",
  district: "",
  state: "",
  pincode: "",
  country: "",
  coordinates: null,
});
const getDefaultCarePlan = () => ({
  serviceCategory: "",
  nurseSpecialist: "",
  physioModule: [],
  physioDurationType: "Days",
  physioDurationValue: "",
  physioFrequency: "",
  patientInstructions: "",
  nurseType: "",
  nurseRegularDutyKey: "",
  nurseRegularServices: [],
  nurseOneTimeServices: [],
  nurseDays: "",
});

const normalizeGender = (value) => (String(value || "").toLowerCase() === "female" ? "female" : "male");

const splitPhoneParts = (raw, fallbackCode = "91") => {
  const digits = String(raw || "").replace(/\D/g, "");
  if (!digits) return { code: fallbackCode, number: "" };
  if (digits.length > 10) {
    return {
      code: digits.slice(0, digits.length - 10) || fallbackCode,
      number: digits.slice(-10),
    };
  }
  return { code: fallbackCode, number: digits.slice(0, 10) };
};

const mapPatientToProfile = (patient) => {
  const fallback = getDefaultProfile();
  if (!patient) return fallback;

  const pInfo = patient?.personalInfo || {};
  const basicInfo = pInfo?.basicInfo || {};
  const addrInfo = pInfo?.addressInfo || basicInfo?.addressInfo || patient?.addressInfo || {};
  const nameInfo = basicInfo?.nameInfo || {};
  const otherInfo = basicInfo?.otherInfo || {};

  const dobValue = otherInfo?.dob || basicInfo?.dob || patient?.dob || "";
  const parsedDob = dobValue ? new Date(dobValue) : null;
  const dobStr =
    parsedDob && !Number.isNaN(parsedDob.getTime()) ? parsedDob.toISOString().split("T")[0] : "";

  const primaryPhone = otherInfo?.phone || basicInfo?.phone || patient?.phone || "";
  const emergencyPhone = patient?.emergencyContactInfo?.[0]?.phone || "";
  const primaryParts = splitPhoneParts(primaryPhone, "91");
  const emergencyParts = splitPhoneParts(emergencyPhone, primaryParts.code || "91");

  return {
    abhaNumber: patient?.ABHA?.number || fallback.abhaNumber,
    mrn: patient?.mrn || patient?.patientID || fallback.mrn,
    title: nameInfo?.title || patient?.title || fallback.title,
    name: nameInfo?.fullName || nameInfo?.name || patient?.name || fallback.name,
    dob: dobStr || fallback.dob,
    gender: normalizeGender(otherInfo?.gender || basicInfo?.gender || patient?.gender),
    phoneCode: primaryParts.code || fallback.phoneCode,
    phoneNumber: primaryParts.number || fallback.phoneNumber,
    emergencyCode: emergencyParts.code || fallback.emergencyCode,
    emergencyNumber: emergencyParts.number || fallback.emergencyNumber,
    flatNo: addrInfo?.unitNumber || patient?.flatNo || fallback.flatNo,
    address: addrInfo?.address1 || addrInfo?.area || patient?.address || fallback.address,
    city: addrInfo?.city || patient?.city || fallback.city,
    district: addrInfo?.district || patient?.district || fallback.district,
    state: addrInfo?.state || patient?.state || fallback.state,
    pincode: String(addrInfo?.pincode || patient?.pincode || ""),
    country: addrInfo?.country || patient?.country || "India",
    coordinates: addrInfo?.coordinates || fallback.coordinates,
  };
};

export default function AddPatientModal({
  isOpen,
  onClose,
  onSave,
  onUploadDischargeSummary,
  mode = "add",
  initialPatient = null,
  existingDischargeSummaries = [],
  existingDischargeSummariesLoading = false,
  existingDischargeSummariesError = "",
}) {
  const [currentStep, setCurrentStep] = useState(1);
  const [createdPatientId, setCreatedPatientId] = useState("");
  const [isUploadingDischargeSummary, setIsUploadingDischargeSummary] = useState(false);
  const [isSubmittingCarePlan, setIsSubmittingCarePlan] = useState(false);
  const isAddMode = mode === "add";
  const isEditMode = mode === "edit";
  const isPrescribeMode = mode === "prescribe";
  const modalTitle = isEditMode ? "Edit Patient" : isPrescribeMode ? "Prescription" : "Add New Patient";


  const [profile, setProfile] = useState(getDefaultProfile());

  const [casedata, setCase] = useState([]);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [isSearchingAddress, setIsSearchingAddress] = useState(false);
  const addressRef = useRef(null);
  const [errors, setErrors] = useState({});
  const [isCheckingPhone, setIsCheckingPhone] = useState(false);


  const [pdfTitle, setPdfTitle] = useState("");
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState("");
  const [pdfError, setPdfError] = useState("");
  const [selectedExistingSummary, setSelectedExistingSummary] = useState(null);
  const [selectedDischargeSummaryId, setSelectedDischargeSummaryId] = useState("");

  const [carePlan, setCarePlan] = useState(getDefaultCarePlan());


  const [pricingLoading, setPricingLoading] = useState(false);
  const [pricingError, setPricingError] = useState("");
  const [pricing, setPricing] = useState({
    partnerServices: [],
    nurseRegularSlots: [],
    nurseOneTimeServices: [],
    physioTreatments: [],
    physioAssessmentFee: 0,
  });

  const resolveDischargeSummaryId = (summary) => {
    const raw = summary?._id || summary?.id || "";
    if (!raw) return "";
    if (typeof raw === "object" && raw?.$oid) return String(raw.$oid);
    return String(raw);
  };

  const getDischargeSummarySelectionKey = (summary) =>
    resolveDischargeSummaryId(summary) || String(summary?.key || "");

  const extractUploadedDischargeSummaryId = (uploadRes) => {
    const summaries = Array.isArray(uploadRes?.data?.dischargeSummaries)
      ? uploadRes.data.dischargeSummaries
      : [];
    if (!summaries.length) return "";
    const latest = summaries[summaries.length - 1];
    return resolveDischargeSummaryId(latest);
  };

  const clearExistingSummarySelection = () => {
    setSelectedExistingSummary(null);
    setSelectedDischargeSummaryId("");
  };

  const handleExistingSummarySelection = (summary) => {
    const clickedKey = getDischargeSummarySelectionKey(summary);
    const currentKey = getDischargeSummarySelectionKey(selectedExistingSummary);
    const shouldClear = clickedKey && currentKey && clickedKey === currentKey;
    if (shouldClear) {
      clearExistingSummarySelection();
      return;
    }
    setSelectedExistingSummary(summary);
    setSelectedDischargeSummaryId(resolveDischargeSummaryId(summary));
  };


  useEffect(() => {
    if (isOpen) {
      const presetPatientId = resolvePatientRecordId(initialPatient);

      setCurrentStep(isPrescribeMode ? 2 : 1);
      setCreatedPatientId(isPrescribeMode || isEditMode ? String(presetPatientId || "") : "");
      setProfile(isEditMode ? mapPatientToProfile(initialPatient) : getDefaultProfile());

      setPdfTitle("");
      setPdfFile(null);
      setPdfPreviewUrl("");
      setPdfError("");
      setSelectedExistingSummary(null);
      setSelectedDischargeSummaryId("");

      setCarePlan(getDefaultCarePlan());

      loadLookupData();
      fetchPartnerPricing();
    }

  }, [isOpen, isPrescribeMode, isEditMode, initialPatient]);

  useEffect(() => {
    if (!isOpen || !isPrescribeMode) return;
    if (!Array.isArray(existingDischargeSummaries) || existingDischargeSummaries.length === 0) {
      setSelectedExistingSummary(null);
      return;
    }
    setSelectedExistingSummary((prev) => {
      const prevKey = getDischargeSummarySelectionKey(prev);
      if (!prevKey) return null;
      const stillExists = existingDischargeSummaries.find(
        (item) => getDischargeSummarySelectionKey(item) === prevKey
      );
      return stillExists || null;
    });
  }, [isOpen, isPrescribeMode, existingDischargeSummaries]);

  useEffect(() => {
    if (!isOpen || !isEditMode) return;
    const patientId = resolvePatientRecordId(initialPatient);
    if (!patientId) return;

    let cancelled = false;
    (async () => {
      try {
        const freshRes = await getPatientById(patientId);
        if (!cancelled && freshRes?.error === false) {
          setProfile(mapPatientToProfile(freshRes?.data));
        }
      } catch (error) {
        console.error("Error fetching patient details for edit:", error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, isEditMode, initialPatient]);


  useEffect(() => {
    const handleClickOutside = (e) => {
      if (addressRef.current && !addressRef.current.contains(e.target)) {
        setAddressSuggestions([]);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const loadLookupData = async () => {
    try {
      const result = await getLookupData();
      if (result?.error === false) {
        setCase(Array.isArray(result?.data) ? result.data : []);
      }
    } catch (error) {
      console.error("Error fetching lookup data:", error);
    }
  };

  const fetchPartnerPricing = async () => {
    try {
      setPricingLoading(true);
      setPricingError("");

      const res = await getPartner();
      if (!res || res?.error) {
        setPricingError(res?.message || "Failed to fetch partner pricing");
        return;
      }

      const data = res?.data || {};
      const partnerServices = safeArr(data?.services)
        .map((item) => extractPartnerServiceString(item))
        .filter(Boolean);

      const nurseRegularSlots = safeArr(data?.pricingInfo?.nurseService?.regularShifts?.slots)
        .map((x) => {
          const mappedServices = safeArr(x?.services)
            .map((item) => {
              if (typeof item === "string") return item.trim();
              return String(item?.service || item?.name || item?.label || item?.serviceType || "").trim();
            })
            .filter(Boolean);
          return {
            shift: x?.shift,
            type: x?.type,
            fee: getDiscountedFee(x?.fee ?? x?.fees, x?.discount),
            services: mappedServices,
            foodAccommodation: safeArr(x?.foodAccommodation),
            travelAllowance: safeArr(x?.travelAllowance),
          };
        })
        .filter((x) => x.shift && x.type && x.fee > 0);

      const nurseOneTimeServices = safeArr(data?.pricingInfo?.nurseService?.oneTimeShifts?.services)
        .map((x) => ({
          serviceType: x?.serviceType,
          fee: getDiscountedFee(x?.fee ?? x?.fees, x?.discount),
          notes: String(x?.notes || "").trim(),
        }))
        .filter((x) => x.serviceType && x.fee > 0);

      const physioNode =
        data?.pricingInfo?.physioService ||
        data?.pricingInfo?.physioServices ||
        data?.pricingInfo?.physio_service ||
        {};

      const physioAssessmentFee = toNumber(physioNode?.assessmentFees);

      const physioTreatments = safeArr(physioNode?.services)
        .map((x) => ({
          serviceType: x?.name || x?.serviceType || x?.label || "",
          fee: getDiscountedFee(x?.fee ?? x?.fees, x?.discount),
          notes: String(x?.notes || "").trim(),
        }))
        .filter((x) => x.serviceType && x.fee > 0);

      setPricing({
        partnerServices,
        nurseRegularSlots,
        nurseOneTimeServices,
        physioTreatments,
        physioAssessmentFee,
      });
    } catch (e) {
      console.error("fetchPartnerPricing error:", e);
      setPricingError("Failed to fetch partner pricing");
    } finally {
      setPricingLoading(false);
    }
  };

  const titleOptions = Array.isArray(casedata)
    ? casedata.filter((item) => item.type === "title" && item.label !== "Master.")
    : [];

  const phoneCodeOptions = Array.isArray(casedata)
    ? casedata.filter((item) => item.type === "phonecode")
    : [];

  const normalizePhoneCodeValue = (raw) => {
    const val = String(raw ?? "").trim();
    if (!val) return "91";
    return val.replace(/^\+/, "");
  };

  const getPhoneCodeLabel = (item) => {
    const label = String(item?.label ?? "").trim();
    if (label) return label;
    const value = normalizePhoneCodeValue(item?.value);
    return `+${value}`;
  };

  useEffect(() => {
    if (!isOpen) return;
    const titleOpts = Array.isArray(casedata)
      ? casedata.filter((item) => item.type === "title" && item.label !== "Master.")
      : [];
    const phoneOpts = Array.isArray(casedata) ? casedata.filter((item) => item.type === "phonecode") : [];
    if (!titleOpts.length && !phoneOpts.length) return;

    const allowedTitleValues = new Set(titleOpts.map((item) => String(item?.value ?? "").trim()).filter(Boolean));
    const allowedDialCodes = new Set(phoneOpts.map((item) => normalizePhoneCodeValue(item?.value)));
    const fallbackTitle = titleOpts[0]?.value || "Mr";
    const fallbackCode = normalizePhoneCodeValue(phoneOpts[0]?.value || "91");

    setProfile((prev) => ({
      ...prev,
      title: allowedTitleValues.size && !allowedTitleValues.has(String(prev.title || "").trim()) ? fallbackTitle : prev.title,
      phoneCode: allowedDialCodes.size && !allowedDialCodes.has(prev.phoneCode) ? fallbackCode : prev.phoneCode,
      emergencyCode: allowedDialCodes.size && !allowedDialCodes.has(prev.emergencyCode) ? fallbackCode : prev.emergencyCode,
    }));
  }, [isOpen, casedata]);



  const nurseRegularDutyOptions = useMemo(() => {
    return (pricing.nurseRegularSlots || []).map((x) => ({
      value: dutyKey(x.shift, x.type),
      label: `${x.type} Duty - ${shiftLabel(x.shift)}`,
      fee: x.fee,
      shift: x.shift,
      type: x.type,
      services: safeArr(x.services),
      foodAccommodation: safeArr(x.foodAccommodation),
      travelAllowance: safeArr(x.travelAllowance),
    }));
  }, [pricing.nurseRegularSlots]);

  const selectedRegularDuty = useMemo(() => {
    if (!carePlan.nurseRegularDutyKey) return null;
    return (
      (nurseRegularDutyOptions || []).find((opt) => opt.value === carePlan.nurseRegularDutyKey) ||
      null
    );
  }, [carePlan.nurseRegularDutyKey, nurseRegularDutyOptions]);

  const nurseRegularLookupServiceOptions = useMemo(() => {
    const normalize = (value) => String(value || "").trim().toLowerCase();
    const normalizeSubRole = (value) => normalize(value).replace(/\s+/g, "-");
    const extractLookupServiceValues = (item) => {
      const valuesFromArray = safeArr(item?.values)
        .map((entry) => {
          if (typeof entry === "string") return entry.trim();
          if (entry && typeof entry === "object") {
            return String(
              entry?.value ??
                entry?.label ??
                entry?.name ??
                entry?.serviceType ??
                ""
            ).trim();
          }
          return "";
        })
        .filter(Boolean);

      const fallbackValues = [item?.value, item?.label]
        .map((entry) => String(entry || "").trim())
        .filter(Boolean);

      const merged = valuesFromArray.length ? valuesFromArray : fallbackValues;
      return Array.from(new Set(merged));
    };
    const selectedSpecialist = String(carePlan.nurseSpecialist || "").trim();
    if (!selectedSpecialist) return [];
    const selectedSubRole =
      normalizeSubRole(selectedSpecialist) === "nursing-assistant"
        ? "nursing-assistant"
        : "nurse";

    const seen = new Set();
    return safeArr(casedata)
      .filter((item) => normalize(item?.type) === "services")
      .filter((item) => normalize(item?.role) === "nurse")
      .filter((item) => {
        const lookupSubRole = normalizeSubRole(item?.subRole);
        if (selectedSubRole === "nursing-assistant") {
          return lookupSubRole === "nursing-assistant";
        }
        return true;
      })
      .sort((a, b) => {
        const posA = Number(a?.position ?? Number.MAX_SAFE_INTEGER);
        const posB = Number(b?.position ?? Number.MAX_SAFE_INTEGER);
        if (posA !== posB) return posA - posB;
        return String(a?.label || a?.value || "").localeCompare(
          String(b?.label || b?.value || "")
        );
      })
      .flatMap((item) => {
        const extractedValues = extractLookupServiceValues(item);
        return extractedValues.map((serviceName) => {
          const value = String(serviceName || "").trim();
          const label = value;
          if (!value) return null;
          const dedupeKey = `${value}__${label}`;
          if (seen.has(dedupeKey)) return null;
          seen.add(dedupeKey);
          return { value, label };
        });
      })
      .filter(Boolean);
  }, [casedata, carePlan.nurseSpecialist]);

  const nurseRegularServiceOptions = useMemo(() => {
    const existing = new Set(
      (nurseRegularLookupServiceOptions || []).map((item) => String(item?.value || ""))
    );
    const manual = (carePlan.nurseRegularServices || [])
      .map((item) => String(item || "").trim())
      .filter(Boolean)
      .filter((value) => !existing.has(value))
      .map((value) => ({ value, label: value }));
    return [...(nurseRegularLookupServiceOptions || []), ...manual];
  }, [nurseRegularLookupServiceOptions, carePlan.nurseRegularServices]);

  const nurseOneTimeServiceOptions = useMemo(() => {
    return (pricing.nurseOneTimeServices || []).map((x) => ({
      value: x.serviceType,
      label: x.serviceType,
      fee: x.fee,
      notes: x.notes || "",
    }));
  }, [pricing.nurseOneTimeServices]);

  const physioModuleOptions = useMemo(() => {
    return (pricing.physioTreatments || []).map((x) => ({
      value: x.serviceType,
      label: x.serviceType,
      fee: x.fee,
      notes: x.notes || "",
    }));
  }, [pricing.physioTreatments]);

  const serviceCategoryOptions = useMemo(() => {
    const fromPartnerServices = mapPartnerServicesToCareCategories(pricing?.partnerServices);
    if (fromPartnerServices.length) return fromPartnerServices;

    const inferred = [];
    if ((pricing?.nurseRegularSlots || []).length || (pricing?.nurseOneTimeServices || []).length) {
      inferred.push({ value: "Nursing Care", label: "Nursing Care" });
    }
    if ((pricing?.physioTreatments || []).length) {
      inferred.push({ value: "Physio", label: "Physiotherapy" });
    }
    return inferred;
  }, [
    pricing?.partnerServices,
    pricing?.nurseRegularSlots,
    pricing?.nurseOneTimeServices,
    pricing?.physioTreatments,
  ]);

  const physioFrequencyOptions = useMemo(() => {
    const allOptions = [
      { value: "Every day", label: "Daily" },
      { value: "Alternative days", label: "Alternative Days" },
      { value: "Twice in week", label: "Twice in a Week" },
      { value: "Thrice in week", label: "Thrice in a Week" },
    ];

    const durationValue = Number(carePlan.physioDurationValue || 0);
    if (!Number.isFinite(durationValue) || durationValue <= 0) return allOptions;

    const durationType = String(carePlan.physioDurationType || "Days");
    let totalDays = durationValue;
    if (durationType === "Weeks") totalDays = durationValue * 7;
    if (durationType === "Months") totalDays = durationValue * 30;

    if (totalDays > 5) return allOptions;
    if (totalDays >= 3) return allOptions.slice(0, 3);
    return allOptions.slice(0, 2);
  }, [carePlan.physioDurationType, carePlan.physioDurationValue]);

  useEffect(() => {
    if (carePlan.serviceCategory !== "Physio") return;
    if (!carePlan.physioFrequency) return;
    const exists = physioFrequencyOptions.some((opt) => opt.value === carePlan.physioFrequency);
    if (!exists) {
      setCarePlan((prev) => ({ ...prev, physioFrequency: "" }));
    }
  }, [carePlan.serviceCategory, carePlan.physioFrequency, physioFrequencyOptions]);

  useEffect(() => {
    if (!carePlan.serviceCategory) return;
    const stillSupported = serviceCategoryOptions.some(
      (option) => option.value === carePlan.serviceCategory
    );
    if (stillSupported) return;

    setCarePlan((prev) => ({
      ...prev,
      serviceCategory: "",
      nurseSpecialist: "",
      physioModule: [],
      physioDurationType: "Days",
      physioDurationValue: "",
      physioFrequency: "",
      nurseType: "",
      nurseRegularDutyKey: "",
      nurseRegularServices: [],
      nurseOneTimeServices: [],
      nurseDays: "",
    }));
  }, [carePlan.serviceCategory, serviceCategoryOptions]);


  if (!isOpen) return null;



  const handleProfileChange = (e) => {
    let { name, value } = e.target;
    if (isEditMode && (name === "phoneCode" || name === "phoneNumber")) {
      return;
    }
    if (name === "phoneNumber" || name === "emergencyNumber") {
      const onlyDigits = value.replace(/\D/g, "");
      value = onlyDigits.length > 10 ? onlyDigits.slice(-10) : onlyDigits;
    }

    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    if (name === "phoneCode" || name === "phoneNumber") {
      setErrors((prev) => {
        if (!prev.phoneNumber) return prev;
        const newErrors = { ...prev };
        delete newErrors.phoneNumber;
        return newErrors;
      });
    }

    setProfile((prev) => ({ ...prev, [name]: value }));
  };

  const onAddressChange = async (e) => {
    const query = e.target.value;

    if (errors.address) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.address;
        return newErrors;
      });
    }

    setProfile((prev) => ({
      ...prev,
      address: query,
      city: "",
      district: "",
      state: "",
      pincode: "",
      coordinates: null,
    }));

    if (query.length >= 3) {
      setIsSearchingAddress(true);
      try {
        const results = await searchPlaces(query);
        setAddressSuggestions(results);
      } catch (err) {
        setAddressSuggestions([]);
      } finally {
        setIsSearchingAddress(false);
      }
    } else {
      setAddressSuggestions([]);
    }
  };

  const handleSelectAddressSuggestion = async (suggestion) => {
    setAddressSuggestions([]);
    setProfile((prev) => ({ ...prev, address: suggestion.description }));

    try {
      const details = await getPlaceDetails(suggestion.place_id);
      if (details) {
        const components = details.address_components || [];
        let city = "";
        let district = "";
        let state = "";
        let country = "";
        let pincode = "";

        components.forEach((c) => {
          if (c.types.includes("locality") || c.types.includes("sublocality")) city = c.short_name;
          if (c.types.includes("administrative_area_level_2")) district = c.short_name;
          if (c.types.includes("administrative_area_level_1")) state = c.short_name;
          if (c.types.includes("country")) country = c.short_name;
          if (c.types.includes("postal_code")) pincode = c.short_name;
        });

        setProfile((prev) => ({
          ...prev,
          address: suggestion.description,
          city,
          district: district || city,
          state,
          country: country || "India",
          pincode,
          coordinates: details.geometry?.location
            ? { lat: details.geometry.location.lat, lng: details.geometry.location.lng }
            : null,
        }));
      }
    } catch (error) {
      console.error("Error fetching Ola details:", error);
    }
  };


  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.type === "application/pdf") {
      if (file.size > MAX_PDF_SIZE_BYTES) {
        setPdfFile(null);
        setPdfPreviewUrl("");
        setPdfError("PDF size must be 10MB or less.");
        alert("PDF size must be 10MB or less.");
        return;
      }
      setPdfError("");
      setPdfFile(file);
      setPdfPreviewUrl(URL.createObjectURL(file));
    } else {
      setPdfError("Please upload a valid PDF file.");
      alert("Please upload a valid PDF file.");
    }
  };


  const handleCarePlanChange = (e) => {
    const { name, value } = e.target;

    if (name === "serviceCategory") {
      setCarePlan((prev) => ({
        ...prev,
        serviceCategory: value,
        nurseSpecialist: "",
        physioModule: [],
        physioDurationType: "Days",
        physioDurationValue: "",
        physioFrequency: "",
        nurseType: "",
        nurseRegularDutyKey: "",
        nurseRegularServices: [],
        nurseOneTimeServices: [],
        nurseDays: "",
      }));
      return;
    }

    if (name === "physioDurationValue" || name === "nurseDays") {
      const onlyDigits = String(value || "").replace(/\D/g, "").slice(0, 2);
      setCarePlan((prev) => ({ ...prev, [name]: onlyDigits }));
      return;
    }

    if (name === "nurseType") {
      setCarePlan((prev) => ({
        ...prev,
        nurseType: value,
        nurseRegularDutyKey: "",
        nurseRegularServices: [],
        nurseOneTimeServices: [],
        nurseDays: "",
      }));
      return;
    }

    if (name === "nurseSpecialist") {
      setCarePlan((prev) => ({
        ...prev,
        nurseSpecialist: value,
        nurseRegularServices: [],
      }));
      return;
    }

    setCarePlan((prev) => ({ ...prev, [name]: value }));
  };

  const handleNurseOneTimeServicesMulti = (selected) => {
    const values = selected ? selected.map((x) => x.value) : [];
    setCarePlan((prev) => ({ ...prev, nurseOneTimeServices: values }));
  };

  const handleNurseRegularServicesMulti = (selected) => {
    const values = selected ? selected.map((x) => x.value) : [];
    setCarePlan((prev) => ({ ...prev, nurseRegularServices: values }));
  };

  const handleCreateNurseRegularService = (inputValue) => {
    const value = String(inputValue || "").trim();
    if (!value) return;
    setCarePlan((prev) => {
      const current = Array.isArray(prev.nurseRegularServices)
        ? prev.nurseRegularServices.map((item) => String(item || "").trim()).filter(Boolean)
        : [];
      if (current.includes(value)) return prev;
      return { ...prev, nurseRegularServices: [...current, value] };
    });
  };

  const handlePhysioServicesMulti = (selected) => {
    const values = selected ? selected.map((x) => x.value) : [];
    setCarePlan((prev) => ({ ...prev, physioModule: values }));
  };

  const validateStep1 = ({ skipPhoneValidation = false } = {}) => {
    const newErrors = {};
    const trimmedName = String(profile.name || "").trim();
    if (!trimmedName) {
      newErrors.name = "Required";
    } else if (!isValidName(trimmedName)) {
      newErrors.name = "Name must be 2-50 letters and spaces only";
    }

    const dobValue = String(profile.dob || "").trim();
    if (!dobValue) {
      newErrors.dob = "Required";
    } else {
      const parsedDob = moment(dobValue, "YYYY-MM-DD", true);
      const { minDobMoment, maxDobMoment } = getDobBounds();
      if (!parsedDob.isValid()) {
        newErrors.dob = "Invalid DOB";
      } else if (parsedDob.isAfter(maxDobMoment, "day")) {
        newErrors.dob = "Invalid DOB";
      } else if (parsedDob.isBefore(minDobMoment, "day")) {
        newErrors.dob = "Invalid DOB";
      }
    }

    if (!skipPhoneValidation && (!profile.phoneNumber || profile.phoneNumber.length < 10)) {
      newErrors.phoneNumber = "Valid 10-digit number required";
    }

    const trimmedAddress = String(profile.address || "").trim();
    if (!trimmedAddress) {
      newErrors.address = "Required";
    } else if (!hasValidCoordinates(profile.coordinates)) {
      newErrors.address = "Please select an address from suggestions";
    }

    return newErrors;
  };

  const resolvePatientRecordId = (data) => data?._id || data?.id || data?.userID || data?.patientID || "";

  const buildPayload = () => {
    const cleanedPhoneCode = String(profile.phoneCode || "").replace("+", "");
    const fullPhone = Number(cleanedPhoneCode + profile.phoneNumber);

    const fullEmergencyPhone = profile.emergencyNumber
      ? Number(String(profile.emergencyCode || "").replace("+", "") + profile.emergencyNumber)
      : null;

    return {
      ABHA: {
        number: profile.abhaNumber,
        isConnected: false,
        ...(profile.abhaNumber ? { lastUpdated: new Date() } : {}),
      },
      mrn: profile.mrn ? String(profile.mrn).trim() : "",
      phone: fullPhone,
      emergencyContactInfo: fullEmergencyPhone ? [{ name: profile.name, phone: fullEmergencyPhone }] : [],
      personalInfo: {
        completed: true,
        basicInfo: {
          nameInfo: { title: profile.title, fullName: profile.name },
          otherInfo: { phone: fullPhone, gender: profile.gender, dob: new Date(profile.dob) },
        },
        addressInfo: {
          unitNumber: profile.flatNo,
          address1: profile.address,
          address2: "",
          city: profile.city || "",
          district: profile.district || "",
          state: profile.state || "",
          pincode: profile.pincode ? Number(profile.pincode) : null,
          country: profile.country || "India",
          landmark: "",
          ...(profile.coordinates ? { coordinates: profile.coordinates } : {}),
        },
      },
    };
  };

  const buildUpdatePayload = (patientId) => {
    const fullEmergencyPhone = profile.emergencyNumber
      ? Number(String(profile.emergencyCode || "").replace("+", "") + profile.emergencyNumber)
      : null;
    const parsedDob = profile.dob ? new Date(profile.dob) : null;
    const hasValidDob = parsedDob && !Number.isNaN(parsedDob.getTime());
    const otherInfo = {
      gender: profile.gender,
      ...(hasValidDob ? { dob: parsedDob } : {}),
    };

    return {
      _id: patientId,
      mrn: profile.mrn ? String(profile.mrn).trim() : "",
      personalInfo: {
        completed: true,
        basicInfo: {
          nameInfo: { title: profile.title, fullName: profile.name },
          otherInfo,
        },
        addressInfo: {
          unitNumber: profile.flatNo,
          address1: profile.address,
          city: profile.city || "",
          district: profile.district || "",
          state: profile.state || "",
          pincode: profile.pincode ? Number(profile.pincode) : null,
          country: profile.country || "India",
          ...(profile.coordinates ? { coordinates: profile.coordinates } : {}),
        },
      },
      emergencyContactInfo: fullEmergencyPhone ? [{ name: profile.name, phone: fullEmergencyPhone }] : [],
    };
  };

  const validatePhoneUniqueness = async () => {
    if (!isAddMode || createdPatientId) return true;

    const dialCode = String(profile.phoneCode || "").replace("+", "");
    const localNumber = String(profile.phoneNumber || "").trim();
    if (!dialCode || localNumber.length < 10) return true;

    const fullPhone = Number(`${dialCode}${localNumber}`);
    if (!Number.isFinite(fullPhone)) return true;

    setIsCheckingPhone(true);
    try {
      const checkRes = await checkPhoneExists(fullPhone);
      if (!checkRes || typeof checkRes !== "object") {
        alert("Unable to verify phone number now. Please try again.");
        return false;
      }
      if (checkRes?.error === false && checkRes?.data?.allow === false) {
        setErrors((prev) => ({ ...prev, phoneNumber: "Phone number already exists" }));
        return false;
      }
      if (checkRes?.error === true) {
        alert(checkRes?.message || "Unable to verify phone number now. Please try again.");
        return false;
      }
      return true;
    } catch (error) {
      console.error("Phone check failed:", error);
      alert("Unable to verify phone number now. Please try again.");
      return false;
    } finally {
      setIsCheckingPhone(false);
    }
  };

  const getDischargeUploadState = () => {
    const trimmedPdfTitle = pdfTitle.trim();
    const hasAnyUploadInput = Boolean(trimmedPdfTitle || pdfFile);
    const hasCompleteNewUpload = Boolean(trimmedPdfTitle && pdfFile);
    return { trimmedPdfTitle, hasAnyUploadInput, hasCompleteNewUpload };
  };

  const persistAddFlowProfileAndDischarge = async ({
    finalizeAddFlow = false,
    includeDischargeSummary = true,
  } = {}) => {
    if (!onSave || (includeDischargeSummary && !onUploadDischargeSummary)) {
      return { ok: false, patientId: "", dischargeSummaryId: "" };
    }

    const existingPatientId = String(createdPatientId || "");
    const savePayload = existingPatientId ? buildUpdatePayload(existingPatientId) : buildPayload();
    if (existingPatientId) {
      savePayload._id = existingPatientId;
      savePayload.id = existingPatientId;
      savePayload.userID = existingPatientId;
    }

    const saveRes = await onSave(savePayload, { suppressSuccessToast: true });
    if (!saveRes || saveRes.error !== false) {
      return { ok: false, patientId: "", dischargeSummaryId: "" };
    }

    const resolvedPatientId = String(resolvePatientRecordId(saveRes?.data) || existingPatientId || "");
    if (!resolvedPatientId) {
      alert("Unable to resolve patient ID. Please try again.");
      return { ok: false, patientId: "", dischargeSummaryId: "" };
    }
    setCreatedPatientId(resolvedPatientId);

    if (!includeDischargeSummary) {
      setSelectedExistingSummary(null);
      setSelectedDischargeSummaryId("");
      return {
        ok: true,
        patientId: resolvedPatientId,
        dischargeSummaryId: "",
      };
    }

    const uploadRes = await onUploadDischargeSummary(
      {
        patientID: resolvedPatientId,
        title: pdfTitle.trim(),
        file: pdfFile,
      },
      { finalizeAddFlow }
    );
    if (!uploadRes || uploadRes.error !== false) {
      return { ok: false, patientId: resolvedPatientId, dischargeSummaryId: "" };
    }

    const uploadedSummaryId = extractUploadedDischargeSummaryId(uploadRes);
    setSelectedExistingSummary(null);
    setSelectedDischargeSummaryId(uploadedSummaryId || "");

    return {
      ok: true,
      patientId: resolvedPatientId,
      dischargeSummaryId: String(uploadedSummaryId || ""),
    };
  };

  const resetAddDraftState = () => {
    setCurrentStep(1);
    setCreatedPatientId("");
    setProfile(getDefaultProfile());
    setPdfTitle("");
    setPdfFile(null);
    setPdfPreviewUrl("");
    setPdfError("");
    setSelectedExistingSummary(null);
    setSelectedDischargeSummaryId("");
    setCarePlan(getDefaultCarePlan());
    setErrors({});
    setAddressSuggestions([]);
  };

  const handleRequestClose = () => {
    if (!isAddMode) {
      onClose();
      return;
    }

    const shouldDiscard = window.confirm("Data will not be saved. Do you want to discard?");
    if (!shouldDiscard) return;

    resetAddDraftState();
    onClose();
  };

  const handleNext = async () => {
    if (currentStep === 1) {
      const step1Errors = validateStep1();
      if (Object.keys(step1Errors).length > 0) {
        setErrors(step1Errors);
        return;
      }
      setErrors({});

      if (isAddMode) {
        const phoneAllowed = await validatePhoneUniqueness();
        if (!phoneAllowed) return;
      }

      if (!isAddMode && onSave) {
        const payload = createdPatientId ? buildUpdatePayload(createdPatientId) : buildPayload();
        if (createdPatientId) {
          payload._id = createdPatientId;
          payload.id = createdPatientId;
          payload.userID = createdPatientId;
        }
        const res = await onSave(payload);
        if (!res || res.error !== false) return;

        const newId = resolvePatientRecordId(res?.data);
        const effectivePatientId = String(newId || createdPatientId || payload?._id || "");
        if (effectivePatientId) setCreatedPatientId(effectivePatientId);

        if (effectivePatientId) {
          try {
            const freshRes = await getPatientById(effectivePatientId);
            if (freshRes && freshRes.error === false) {
              const b = freshRes.data;
              const pInfo = b.personalInfo || {};
              const basicInfo = pInfo.basicInfo || {};
              const addrInfo = pInfo.addressInfo || {};
              const nameInfo = basicInfo.nameInfo || {};
              const otherInfo = basicInfo.otherInfo || {};

              let dobStr = "";
              if (otherInfo.dob) dobStr = new Date(otherInfo.dob).toISOString().split("T")[0];

              const fullPhone = String(otherInfo.phone || "");
              let phoneCode = "91";
              let phoneNumber = "";
              if (fullPhone.length > 10) {
                phoneNumber = fullPhone.slice(-10);
                phoneCode = fullPhone.slice(0, fullPhone.length - 10);
              } else {
                phoneNumber = fullPhone;
              }

              setProfile((prev) => ({
                ...prev,
                abhaNumber: b.ABHA?.number || prev.abhaNumber,
                mrn: b.mrn || b.patientID || prev.mrn,
                title: nameInfo.title || prev.title,
                name: nameInfo.fullName || prev.name,
                dob: dobStr || prev.dob,
                gender: otherInfo.gender || prev.gender,
                phoneCode,
                phoneNumber,
                flatNo: addrInfo.unitNumber || prev.flatNo,
                address: addrInfo.address1 || prev.address,
                city: addrInfo.city || prev.city,
                district: addrInfo.district || prev.district,
                state: addrInfo.state || prev.state,
                pincode: String(addrInfo.pincode || "") || prev.pincode,
                country: addrInfo.country || prev.country,
                coordinates: addrInfo.coordinates || prev.coordinates,
              }));
            }
          } catch (err) {
            console.error("Error syncing with getPatientById:", err);
          }
        }
      }
    }

    if (currentStep === 2) {
      const { trimmedPdfTitle, hasAnyUploadInput, hasCompleteNewUpload } =
        getDischargeUploadState();
      if (hasCompleteNewUpload && pdfFile?.size > MAX_PDF_SIZE_BYTES) {
        setPdfError("PDF size must be 10MB or less.");
        alert("PDF size must be 10MB or less.");
        return;
      }

      if (isPrescribeMode) {
        if (!createdPatientId) {
          alert("Patient record is missing. Please reopen and try again.");
          return;
        }

        if (hasAnyUploadInput && !hasCompleteNewUpload) {
          alert("Please provide a Document Title and upload a PDF.");
          return;
        }

        if (hasCompleteNewUpload && onUploadDischargeSummary) {
          setIsUploadingDischargeSummary(true);
          try {
            const uploadRes = await onUploadDischargeSummary({
              patientID: createdPatientId,
              title: trimmedPdfTitle,
              file: pdfFile,
            });
            if (!uploadRes || uploadRes.error !== false) return;
            const uploadedSummaryId = extractUploadedDischargeSummaryId(uploadRes);
            setSelectedExistingSummary(null);
            setSelectedDischargeSummaryId(uploadedSummaryId);
          } finally {
            setIsUploadingDischargeSummary(false);
          }
        }
      } else if (isAddMode) {
        const isSkippingDischarge = !hasAnyUploadInput;
        if (!isSkippingDischarge && !hasCompleteNewUpload) {
          alert("Please provide a Document Title and upload a PDF.");
          return;
        }
        if (!onSave) return;

        setIsUploadingDischargeSummary(true);
        try {
          const persisted = await persistAddFlowProfileAndDischarge({
            finalizeAddFlow: false,
            includeDischargeSummary: hasCompleteNewUpload,
          });
          if (!persisted.ok) return;
        } finally {
          setIsUploadingDischargeSummary(false);
        }
      }
    }

    setCurrentStep((prev) => prev + 1);
  };

  const getRoleAndSubRoleForCarePlan = () => {
    const category = String(carePlan.serviceCategory || "").toLowerCase();
    if (category === "physio" || category === "physiotherapy") {
      return { role: "physiotherapist", subRole: "physiotherapist" };
    }
    if (String(carePlan.nurseSpecialist || "").toLowerCase() === "nursing-assistant") {
      return { role: "nurse", subRole: "nursing-assistant" };
    }
    return { role: "nurse", subRole: "nurse" };
  };

  const buildAddressInfoForVisit = () => {
    const fromProfile = {
      address1: profile.address || "",
      address2: "",
      district: profile.district || "",
      city: profile.city || "",
      state: profile.state || "",
      pincode: profile.pincode || "",
      landmark: "",
      coordinates: profile.coordinates || {},
    };
    if (fromProfile.address1) return fromProfile;
    return initialPatient?.personalInfo?.addressInfo || {};
  };

  const submitCarePlanVisit = async (patientId, options = {}) => {
    const category = String(carePlan.serviceCategory || "").trim();
    if (!category) return true; // No care-plan selection yet; keep existing behavior.

    const isNurseCategory = category === "Nursing Care";
    const isPhysioCategory = category === "Physio" || category === "Physiotherapy";
    if (!isNurseCategory && !isPhysioCategory) {
      return true;
    }

    let totalDays = 0;
    let dutyType = "";
    let serviceFees = [];
    let feesPerDay = 0;
    let shiftType = "";
    let visitServices = [];
    let foodAccommodation = [];
    let travelAllowance = [];
    let sessionFrequency = "";
    let sessionDuration = 0;
    let sessionDurationType = "";
    let requestedDate = "";
    let assessmentServices = [];
    let sessionFee = 0;

    if (isPhysioCategory) {
      const selectedModules = Array.isArray(carePlan.physioModule)
        ? carePlan.physioModule
        : [];
      if (!selectedModules.length) {
        alert("Please select at least one service.");
        return false;
      }
      const selectedModuleSet = new Set(
        selectedModules.map((item) => String(item || "").trim()).filter(Boolean)
      );
      const selectedPhysioServices = (physioModuleOptions || [])
        .filter((item) => selectedModuleSet.has(String(item.value)))
        .map((item) => ({
          name: String(item.label || item.value || "").trim(),
          fee: Number(item.fee || 0),
        }))
        .filter((item) => item.name);
      if (!selectedPhysioServices.length) {
        alert("Please select valid service options.");
        return false;
      }

      const durationValue = Number(carePlan.physioDurationValue || 0);
      if (!Number.isFinite(durationValue) || durationValue <= 0) {
        alert("Please enter valid duration.");
        return false;
      }

      const frequencyRaw = String(carePlan.physioFrequency || "").trim();
      if (!frequencyRaw) {
        alert("Please select frequency.");
        return false;
      }

      const frequencyMap = {
        "Every day": "Daily",
        "Alternative days": "Alternative Days",
        "Twice in week": "Twice in a Week",
        "Thrice in week": "Thrice in a Week",
      };
      const durationTypeRaw = String(carePlan.physioDurationType || "Days").trim();
      const durationTypeLower = durationTypeRaw.toLowerCase();
      let durationInDays = durationValue;
      if (durationTypeLower === "weeks") durationInDays = durationValue * 7;
      if (durationTypeLower === "months") durationInDays = durationValue * 30;

      dutyType = "regular";
      totalDays = Math.max(1, Math.round(durationInDays));
      serviceFees = selectedPhysioServices.map((item) => ({
        service: item.name,
        fee: Number(item?.fee || 0),
      }));
      visitServices = selectedPhysioServices.map((item) => item.name);
      assessmentServices = selectedPhysioServices;
      sessionFee = selectedPhysioServices.reduce(
        (sum, item) => sum + Number(item?.fee || 0),
        0
      );
      feesPerDay = sessionFee;
      foodAccommodation = [];
      travelAllowance = [];
      sessionFrequency = frequencyMap[frequencyRaw] || frequencyRaw;
      sessionDuration = durationValue;
      sessionDurationType = durationTypeLower;
      requestedDate = moment().format("YYYY-MM-DD");
    }

    if (isNurseCategory) {
      const nurseSpecialist = String(carePlan.nurseSpecialist || "").trim();
      if (!nurseSpecialist) {
        alert("Please select a specialist.");
        return false;
      }

      const nurseType = String(carePlan.nurseType || "").trim();
      if (!nurseType) {
        alert("Please select a nurse service type.");
        return false;
      }

      totalDays = Number(carePlan.nurseDays || 0);
      if (totalDays <= 0) {
        alert("Please enter valid days.");
        return false;
      }

      if (nurseType === "One Time") {
        dutyType = "oneTime";
        const selectedServices = Array.isArray(carePlan.nurseOneTimeServices)
          ? carePlan.nurseOneTimeServices
          : [];
        if (!selectedServices.length) {
          alert("For Nurse One-Time, please select one-time services.");
          return false;
        }

        const feeByService = new Map(
          (nurseOneTimeServiceOptions || []).map((opt) => [String(opt.value), Number(opt.fee || 0)])
        );

        serviceFees = selectedServices.map((service) => ({
          service,
          fee: feeByService.get(String(service)) || 0,
        }));
        visitServices = serviceFees.map((item) => item.service);
        feesPerDay = serviceFees.reduce((sum, item) => sum + Number(item.fee || 0), 0);
      } else if (nurseType === "Regular") {
        dutyType = "regular";
        if (!selectedRegularDuty) {
          alert("Please select a regular duty type.");
          return false;
        }
        const selectedRegularServices = Array.isArray(carePlan.nurseRegularServices)
          ? carePlan.nurseRegularServices.map((item) => String(item || "").trim()).filter(Boolean)
          : [];
        if (!selectedRegularServices.length) {
          alert("Please select at least one service.");
          return false;
        }
        feesPerDay = Number(selectedRegularDuty?.fee || 0);
        const shift = String(selectedRegularDuty?.shift || "").trim();
        const type = String(selectedRegularDuty?.type || "").trim();
        shiftType = [shift, type].filter(Boolean).join(",");
        visitServices = selectedRegularServices;
        serviceFees = [];
        foodAccommodation = safeArr(selectedRegularDuty?.foodAccommodation);
        travelAllowance = safeArr(selectedRegularDuty?.travelAllowance);
      } else {
        alert("Unsupported nurse service type.");
        return false;
      }
    }

    const { role, subRole } = getRoleAndSubRoleForCarePlan();
    const chosenDischargeSummaryId = String(
      options?.dischargeSummaryId ||
      selectedDischargeSummaryId ||
      resolveDischargeSummaryId(selectedExistingSummary) ||
      ""
    ).trim();

    const visitPayload = {
      role,
      subRole,
      dutyType,
      patientID: patientId,
      days: totalDays,
      feesPerDay,
      services: serviceFees,
      addressInfo: buildAddressInfoForVisit(),
      ...(shiftType ? { shiftType } : {}),
      ...(visitServices.length ? { visitServices } : {}),
      ...(foodAccommodation.length ? { foodAccommodation } : {}),
      ...(travelAllowance.length ? { travelAllowance } : {}),
      ...(String(carePlan.patientInstructions || "").trim()
        ? { instructionsForPatient: String(carePlan.patientInstructions).trim() }
        : {}),
      ...(isPhysioCategory
        ? {
          sessionFrequency,
          sessionDuration,
          sessionDurationType,
          requestedDate,
          assessmentServices,
          sessionFee,
        }
        : {}),
    };
    if (chosenDischargeSummaryId) {
      visitPayload.dischargeSummary = chosenDischargeSummaryId;
    }

    setIsSubmittingCarePlan(true);
    try {
      const visitRes = await createVisitForPatient(visitPayload);
      if (!visitRes || visitRes.error) {
        alert(visitRes?.message || "Failed to save care plan prescription.");
        return false;
      }
      return true;
    } catch (error) {
      console.error("createVisitForPatient error:", error);
      alert("Failed to save care plan prescription.");
      return false;
    } finally {
      setIsSubmittingCarePlan(false);
    }
  };

  const handleSkipCarePlan = () => {
    if (!isAddMode || currentStep !== 3) return;
    const shouldSkip = window.confirm(
      "Care plan will be skipped for now. Do you want to continue?"
    );
    if (!shouldSkip) return;

    showToast("Patient profile saved. Care plan skipped.", "Info");
    onClose();
  };

  const handleSubmit = async () => {
    if (isPrescribeMode) {
      const patientId = String(createdPatientId || resolvePatientRecordId(initialPatient) || "");
      if (!patientId) {
        alert("Patient record is missing. Please close and reopen prescription.");
        return;
      }
      const ok = await submitCarePlanVisit(patientId);
      if (!ok) return;
      showToast("Care plan prescription saved successfully!", "Success");
      onClose();
      return;
    }

    if (!isAddMode) {
      const structuredPayload = buildPayload();
      console.log("Submitting Final Step Data:", structuredPayload);
      onClose();
      return;
    }

    let effectivePatientId = String(createdPatientId || "").trim();
    let effectiveSummaryId = String(
      selectedDischargeSummaryId || resolveDischargeSummaryId(selectedExistingSummary) || ""
    ).trim();

    if (!effectivePatientId) {
      const step1Errors = validateStep1();
      if (Object.keys(step1Errors).length > 0) {
        setErrors(step1Errors);
        setCurrentStep(1);
        return;
      }

      const phoneAllowed = await validatePhoneUniqueness();
      if (!phoneAllowed) {
        setCurrentStep(1);
        return;
      }

      const { hasAnyUploadInput, hasCompleteNewUpload } = getDischargeUploadState();
      if (hasAnyUploadInput && !hasCompleteNewUpload) {
        setCurrentStep(2);
        alert("Please provide a Document Title and upload a PDF.");
        return;
      }
      if (hasCompleteNewUpload && pdfFile.size > MAX_PDF_SIZE_BYTES) {
        setCurrentStep(2);
        setPdfError("PDF size must be 10MB or less.");
        alert("PDF size must be 10MB or less.");
        return;
      }

      setIsUploadingDischargeSummary(true);
      try {
        const persisted = await persistAddFlowProfileAndDischarge({
          finalizeAddFlow: true,
          includeDischargeSummary: hasCompleteNewUpload,
        });
        if (!persisted.ok) {
          setCurrentStep(2);
          return;
        }
        effectivePatientId = String(persisted.patientId || "").trim();
        effectiveSummaryId = String(persisted.dischargeSummaryId || "").trim();
      } finally {
        setIsUploadingDischargeSummary(false);
      }
    }

    if (!effectivePatientId) {
      alert("Patient record is missing. Please try again.");
      return;
    }

    const selectedCategory = String(carePlan.serviceCategory || "").trim();
    if (!selectedCategory) {
      showToast(
        "Please complete Care Plan Prescription or use Skip Care Plan.",
        "Error"
      );
      return;
    }

    const carePlanOk = await submitCarePlanVisit(effectivePatientId, {
      dischargeSummaryId: effectiveSummaryId,
    });
    if (!carePlanOk) return;

    showToast("Care plan prescription saved successfully!", "Success");
    onClose();
  };

  const handleEditUpdate = async () => {
    const patientId = String(createdPatientId || resolvePatientRecordId(initialPatient) || "");
    if (!patientId) {
      alert("Patient record is missing. Please close and reopen edit.");
      return;
    }

    const step1Errors = validateStep1({ skipPhoneValidation: true });
    if (Object.keys(step1Errors).length > 0) {
      setErrors(step1Errors);
      return;
    }
    setErrors({});

    if (!onSave) return;
    const payload = buildUpdatePayload(patientId);
    payload._id = patientId;
    payload.id = patientId;
    payload.userID = patientId;

    const res = await onSave(payload);
    if (res && res.error === false) {
      onClose();
    }
  };

  const { minDobMoment, maxDobMoment } = getDobBounds();
  const minDob = formatDobBound(minDobMoment) || "1900-01-01";
  const today = formatDobBound(maxDobMoment) || moment().format("YYYY-MM-DD");

  const renderStepIndicator = () => {
    const steps = isPrescribeMode
      ? [
        { stepNum: 2, label: "Discharge Summary" },
        { stepNum: 3, label: "Care Plan Prescription" },
      ]
      : [
        { stepNum: 1, label: "Profile" },
        { stepNum: 2, label: "Discharge Summary" },
        { stepNum: 3, label: "Care Plan Prescription" },
      ];
    return (
      <div className="flex items-center justify-between mb-8 relative">
        <div className="absolute left-0 right-0 top-1/2 h-[2px] bg-gray-100 -z-10 transform -translate-y-1/2"></div>
        {steps.map(({ stepNum, label }) => {
          const isActive = currentStep === stepNum;
          const isCompleted = currentStep > stepNum;

          return (
            <div key={`${stepNum}-${label}`} className="flex flex-col items-center bg-white px-4">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-[13px] font-semibold transition-colors duration-300
                  ${isActive
                    ? "bg-[#3E57E8] text-white shadow-md"
                    : isCompleted
                      ? "bg-green-500 text-white"
                      : "bg-gray-100 text-gray-400"
                  }`}
              >
                {isCompleted ? <MdCheckCircle size={18} /> : isPrescribeMode ? stepNum - 1 : stepNum}
              </div>
              <span
                className={`text-[11px] font-poppins mt-2 ${isActive ? "text-[#3E57E8] font-medium" : "text-gray-400"
                  }`}
              >
                {label}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  // ---------------- STEP 1 UI (UNCHANGED) ----------------
  const renderStep1 = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 animate-fadeIn">
      <div>
        <label className="block text-[12px] font-medium text-gray-700 mb-1 font-poppins">ABHA Number</label>
        <input
          type="text"
          name="abhaNumber"
          value={profile.abhaNumber}
          onChange={handleProfileChange}
          className={inputClass}
          placeholder="Enter ABHA number"
        />
      </div>
      <div>
        <label className="block text-[12px] font-medium text-gray-700 mb-1 font-poppins">
          MRN (Medical Record Number)
        </label>
        <input
          type="text"
          name="mrn"
          value={profile.mrn}
          onChange={handleProfileChange}
          className={inputClass}
          placeholder="Enter MRN"
        />
      </div>

      <div>
        <label className="block text-[12px] font-medium text-gray-700 mb-1 font-poppins">
          Title & Patient Name (As in Aadhar Card) <span className="text-red-500">*</span>
        </label>
        <div
          className={`flex border rounded-xl bg-[#F6F8FC] overflow-hidden focus-within:border-[#3E57E8] focus-within:ring-2 focus-within:ring-[#3E57E8]/15 transition-all ${errors.name ? "border-red-500" : "border-gray-200"
            }`}
        >
          <select
            name="title"
            value={profile.title}
            onChange={handleProfileChange}
            className="bg-transparent text-[13px] font-poppins px-3 outline-none border-r border-gray-200 min-w-[70px]"
          >
            {titleOptions.length > 0 ? (
              titleOptions.map((item, index) => (
                <option key={`${item.value}-${index}`} value={item.value}>
                  {item.label}
                </option>
              ))
            ) : (
              <>
                <option value="Mr">Mr</option>
                <option value="Mrs">Mrs</option>
                <option value="Ms">Ms</option>
              </>
            )}
          </select>
          <input
            type="text"
            name="name"
            value={profile.name}
            onChange={handleProfileChange}
            placeholder="Full Name"
            className="bg-transparent text-[13px] font-poppins px-3 py-2.5 outline-none flex-1 w-full"
          />
        </div>
        {errors.name && <p className="text-red-500 text-[10px] mt-1 font-poppins">{errors.name}</p>}
      </div>

      <div>
        <label className="block text-[12px] font-medium text-gray-700 mb-1 font-poppins">
          Date of Birth <span className="text-red-500">*</span>
        </label>
        <input
          type="date"
          name="dob"
          min={minDob}
          max={today}
          value={profile.dob}
          onChange={handleProfileChange}
          className={`${inputClass} ${errors.dob ? "border-red-500" : ""}`}
        />
        {errors.dob && <p className="text-red-500 text-[10px] mt-1 font-poppins">{errors.dob}</p>}
      </div>

      <div>
        <label className="block text-[12px] font-medium text-gray-700 mb-1 font-poppins">
          Gender <span className="text-red-500">*</span>
        </label>
        <select name="gender" value={profile.gender} onChange={handleProfileChange} className={inputClass}>
          <option value="male">Male</option>
          <option value="female">Female</option>
        </select>
      </div>

      <div>
        <label className="block text-[12px] font-medium text-gray-700 mb-1 font-poppins">
          Phone Number <span className="text-red-500">*</span>
        </label>
        <div
          className={`flex border rounded-xl bg-[#F6F8FC] overflow-hidden focus-within:border-[#3E57E8] focus-within:ring-2 focus-within:ring-[#3E57E8]/15 transition-all ${errors.phoneNumber ? "border-red-500" : "border-gray-200"
            }`}
        >
          <select
            name="phoneCode"
            value={profile.phoneCode}
            onChange={handleProfileChange}
            disabled={isEditMode}
            className={`bg-transparent text-[13px] font-poppins px-2 outline-none border-r border-gray-200 ${isEditMode ? "opacity-60 cursor-not-allowed" : ""}`}
          >
            {phoneCodeOptions.length > 0 ? (
              phoneCodeOptions.map((item, index) => {
                const codeValue = normalizePhoneCodeValue(item?.value);
                return (
                  <option key={`${item.value}-${index}`} value={codeValue}>
                    {getPhoneCodeLabel(item)}
                  </option>
                );
              })
            ) : (
              <>
                <option value="91">+91 (IN)</option>
                <option value="1">+1 (US)</option>
                <option value="44">+44 (UK)</option>
              </>
            )}
          </select>
          <input
            type="tel"
            name="phoneNumber"
            value={profile.phoneNumber}
            onChange={handleProfileChange}
            disabled={isEditMode}
            maxLength={10}
            placeholder="10-digit number"
            className={`bg-transparent text-[13px] font-poppins px-3 py-2.5 outline-none flex-1 ${isEditMode ? "opacity-60 cursor-not-allowed" : ""}`}
          />
        </div>
        {errors.phoneNumber && (
          <p className="text-red-500 text-[10px] mt-1 font-poppins">{errors.phoneNumber}</p>
        )}
        {isCheckingPhone ? (
          <p className="text-[#3E57E8] text-[10px] mt-1 font-poppins">Checking phone number...</p>
        ) : null}
      </div>

      <div>
        <label className="block text-[12px] font-medium text-gray-700 mb-1 font-poppins">Emergency Contact</label>
        <div className="flex border border-gray-200 rounded-xl bg-[#F6F8FC] overflow-hidden focus-within:border-[#3E57E8] focus-within:ring-2 focus-within:ring-[#3E57E8]/15 transition-all">
          <select
            name="emergencyCode"
            value={profile.emergencyCode}
            onChange={handleProfileChange}
            className="bg-transparent text-[13px] font-poppins px-2 outline-none border-r border-gray-200"
          >
            {phoneCodeOptions.length > 0 ? (
              phoneCodeOptions.map((item, index) => {
                const codeValue = normalizePhoneCodeValue(item?.value);
                return (
                  <option key={`em-${item.value}-${index}`} value={codeValue}>
                    {getPhoneCodeLabel(item)}
                  </option>
                );
              })
            ) : (
              <>
                <option value="91">+91 (IN)</option>
                <option value="1">+1 (US)</option>
                <option value="44">+44 (UK)</option>
              </>
            )}
          </select>
          <input
            type="tel"
            name="emergencyNumber"
            value={profile.emergencyNumber}
            onChange={handleProfileChange}
            placeholder="10-digit number"
            maxLength={10}
            className="bg-transparent text-[13px] font-poppins px-3 py-2.5 outline-none flex-1"
          />
        </div>
      </div>

      <div>
        <label className="block text-[12px] font-medium text-gray-700 mb-1 font-poppins">Flat No / Door No</label>
        <input
          type="text"
          name="flatNo"
          value={profile.flatNo}
          onChange={handleProfileChange}
          className={inputClass}
          placeholder="e.g. Flat 4B, 2nd Floor"
        />
      </div>

      <div className="md:col-span-2 relative" ref={addressRef}>
        <label className="block text-[12px] font-medium text-gray-700 mb-1 font-poppins">
          Address (Locality/Area) <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          name="address"
          value={profile.address}
          onChange={onAddressChange}
          className={`${inputClass} ${errors.address ? "border-red-500" : ""}`}
          placeholder="Start typing to search location..."
          autoComplete="off"
        />
        {isSearchingAddress && (
          <div className="absolute right-3 top-9 border-2 border-[#3E57E8] border-t-transparent w-4 h-4 rounded-full animate-spin"></div>
        )}
        {addressSuggestions.length > 0 && (
          <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
            {addressSuggestions.map((item, idx) => (
              <div
                key={idx}
                className="px-4 py-2 text-[12px] font-poppins hover:bg-gray-50 cursor-pointer border-b last:border-0"
                onClick={() => handleSelectAddressSuggestion(item)}
              >
                {item.description}
              </div>
            ))}
          </div>
        )}
        {errors.address && <p className="text-red-500 text-[10px] mt-1 font-poppins">{errors.address}</p>}
      </div>
    </div>
  );

  const formatSummaryDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };


  const renderStep2 = () => (
    <div className="flex flex-col animate-fadeIn h-full min-h-[400px]">
      <h3 className="text-[15px] font-semibold text-gray-800 font-poppins mb-4">Discharge Summary</h3>

      {isPrescribeMode && (existingDischargeSummariesLoading || existingDischargeSummariesError || (Array.isArray(existingDischargeSummaries) && existingDischargeSummaries.length > 0)) && (
        <div className="mb-5 rounded-xl border border-gray-200 bg-[#FCFDFF] ">
          <div className="px-3 py-2.5 border-b border-gray-100 flex items-center justify-between">
            <p className="text-[12px] font-semibold text-gray-800 font-poppins">
              Previous Discharge Summaries
            </p>
            {selectedExistingSummary ? (
              <button
                type="button"
                onClick={clearExistingSummarySelection}
                className="text-[11px] text-[#3E57E8] hover:text-[#2E45C7] font-poppins"
              >
                Clear selection
              </button>
            ) : (
              <span className="text-[11px] text-gray-400 font-poppins">No selection</span>
            )}
          </div>

          <div className="p-3">
            {existingDischargeSummariesLoading ? (
              <div className="text-[12px] text-[#6b6b6b] italic">Loading discharge summaries...</div>
            ) : existingDischargeSummariesError ? (
              <div className="text-[12px] text-red-500">{existingDischargeSummariesError}</div>
            ) : Array.isArray(existingDischargeSummaries) && existingDischargeSummaries.length > 0 ? (
              <div className="max-h-[175px] overflow-y-auto rounded-lg border border-gray-100 divide-y divide-gray-100 bg-white">
                {existingDischargeSummaries.map((item, idx) => {
                  const selected =
                    getDischargeSummarySelectionKey(selectedExistingSummary) ===
                    getDischargeSummarySelectionKey(item);
                  return (
                    <button
                      key={item?._id || `${item?.title || "summary"}-${idx}`}
                      type="button"
                      onClick={() => handleExistingSummarySelection(item)}
                      className={`w-full text-left px-3 py-2.5 flex items-center gap-3 transition-colors ${selected ? "bg-[#EEF3FF]" : "hover:bg-gray-50"}`}
                    >
                      <span
                        className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${selected ? "border-[#3E57E8]" : "border-gray-300"}`}
                      >
                        {selected ? <span className="w-2 h-2 rounded-full bg-[#3E57E8]" /> : null}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-[12px] font-medium text-gray-800 font-poppins truncate">
                          {item?.title || "Untitled Summary"}
                        </span>
                        <span className="block text-[11px] text-gray-500 font-poppins">
                          {formatSummaryDate(item?.date || item?.createdAt)}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="text-[12px] text-[#6b6b6b] italic">No discharge summaries uploaded.</div>
            )}


          </div>
        </div>
      )}

      <div className="mb-4">
        <label className="block text-[12px] font-medium text-gray-700 mb-1 font-poppins">
          Document Title
        </label>
        <input
          type="text"
          value={pdfTitle}
          onChange={(e) => setPdfTitle(e.target.value)}
          className={inputClass}
          placeholder="e.g., Post Surgery Report"
        />
        {isPrescribeMode ? (
          <p className="text-[11px] text-gray-400 font-poppins mt-1">
            Optional: select a previous summary, upload a new one, or skip this step.
          </p>
        ) : isAddMode ? (
          <p className="text-[11px] text-gray-400 font-poppins mt-1">
            Optional: upload now or skip this step.
          </p>
        ) : null}
      </div>

      {!pdfFile ? (
        <div className="flex-1 border-2 border-dashed border-gray-300 rounded-2xl flex flex-col items-center justify-center p-6 bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer relative">
          <input
            type="file"
            accept="application/pdf"
            onChange={handleFileUpload}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />
          <div className="w-14 h-14 bg-blue-100 text-[#3E57E8] rounded-full flex items-center justify-center mb-4">
            <MdFileUpload size={28} />
          </div>
          <p className="text-[14px] font-medium text-gray-700 font-poppins">Click or drag PDF file to upload</p>
          <p className="text-[12px] text-gray-400 font-poppins mt-2">Maximum file size: 10MB</p>
        </div>
      ) : (
        <div className="flex flex-col flex-1 border border-gray-200 rounded-2xl overflow-hidden relative group min-h-[300px]">
          <div className="bg-gray-800 text-white px-4 py-3 flex justify-between items-center z-10 shrink-0">
            <span className="text-[13px] font-poppins truncate pr-4">{pdfFile.name} (Preview)</span>
            <button
              onClick={() => {
                setPdfFile(null);
                setPdfPreviewUrl("");
                setPdfTitle("");
                setPdfError("");
              }}
              className="text-[#ff5a5a] hover:text-[#ff3b3b] text-[12px] font-poppins bg-white/10 px-3 py-1 rounded-lg transition-colors cursor-pointer shrink-0"
            >
              Remove
            </button>
          </div>
          <iframe
            src={`${pdfPreviewUrl}#toolbar=0&navpanes=0&scrollbar=0`}
            className="w-full flex-1 border-0 bg-gray-100"
            title="PDF Preview"
          />
        </div>
      )}
      {pdfError ? <p className="text-red-500 text-[11px] mt-2 font-poppins">{pdfError}</p> : null}
    </div>
  );


  const renderStep3 = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 animate-fadeIn">
      <div className="md:col-span-2">
        <label className="block text-[12px] font-medium text-gray-700 mb-1 font-poppins">Service Category</label>
        <select
          name="serviceCategory"
          value={carePlan.serviceCategory}
          onChange={handleCarePlanChange}
          className={inputClass}
          disabled={pricingLoading || serviceCategoryOptions.length === 0}
        >
          <option value="" disabled hidden>
            {pricingLoading
              ? "Loading categories..."
              : serviceCategoryOptions.length
                ? "Select Category"
                : "No service categories configured"}
          </option>
          {serviceCategoryOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {pricingError ? <div className="md:col-span-2 text-[12px] text-red-500 font-poppins">{pricingError}</div> : null}

      {carePlan.serviceCategory === "Physio" && (
        <>
          <div className="md:col-span-2">
            <label className="block text-[12px] font-medium text-gray-700 mb-1 font-poppins">
              Services
            </label>
            <Select
              isMulti
              styles={reactSelectStyles}
              options={physioModuleOptions}
              formatOptionLabel={formatServiceOptionWithNotes}
              value={physioModuleOptions.filter((opt) =>
                (carePlan.physioModule || []).includes(opt.value)
              )}
              onChange={handlePhysioServicesMulti}
              isDisabled={pricingLoading || physioModuleOptions.length === 0}
              placeholder={
                pricingLoading
                  ? "Loading..."
                  : physioModuleOptions.length
                    ? "Select services..."
                    : "No priced physio services"
              }
            />
          </div>

          <div>
            <label className="block text-[12px] font-medium text-gray-700 mb-1 font-poppins">Duration</label>
            <div className="flex border border-gray-200 rounded-xl bg-[#F6F8FC] overflow-hidden focus-within:border-[#3E57E8] focus-within:ring-2 focus-within:ring-[#3E57E8]/15 transition-all">
              <input
                type="number"
                name="physioDurationValue"
                value={carePlan.physioDurationValue}
                onChange={handleCarePlanChange}
                className="bg-transparent text-[13px] font-poppins px-3 py-2.5 outline-none flex-1 border-r border-gray-200"
                placeholder="Number"
                min={1}
                max={99}
              />
              <select
                name="physioDurationType"
                value={carePlan.physioDurationType}
                onChange={handleCarePlanChange}
                className="bg-transparent text-[13px] font-poppins px-3 outline-none min-w-[90px]"
              >
                <option value="Days">Days</option>
                <option value="Weeks">Weeks</option>
                <option value="Months">Months</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-[12px] font-medium text-gray-700 mb-1 font-poppins">Frequency</label>
            <select name="physioFrequency" value={carePlan.physioFrequency} onChange={handleCarePlanChange} className={inputClass}>
              <option value="" disabled hidden>
                Select Frequency
              </option>
              {physioFrequencyOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </>
      )}

      {carePlan.serviceCategory === "Nursing Care" && (
        <>
          <div className="md:col-span-2">
            <h4 className="text-[13px] font-medium text-gray-800 mb-3 font-poppins">Specialist</h4>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="nurseSpecialist"
                  value="nurse"
                  checked={carePlan.nurseSpecialist === "nurse"}
                  onChange={handleCarePlanChange}
                  className="w-4 h-4 text-[#3E57E8] focus:ring-[#3E57E8]"
                />
                <span className="text-[13px] font-poppins text-gray-700">Nurse</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="nurseSpecialist"
                  value="nursing-assistant"
                  checked={carePlan.nurseSpecialist === "nursing-assistant"}
                  onChange={handleCarePlanChange}
                  className="w-4 h-4 text-[#3E57E8] focus:ring-[#3E57E8]"
                />
                <span className="text-[13px] font-poppins text-gray-700">Nursing Assistant</span>
              </label>
            </div>
          </div>

          <div className="md:col-span-2">
            <h4 className="text-[13px] font-medium text-gray-800 mb-3 font-poppins">Service Type</h4>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="nurseType"
                  value="Regular"
                  checked={carePlan.nurseType === "Regular"}
                  onChange={handleCarePlanChange}
                  className="w-4 h-4 text-[#3E57E8] focus:ring-[#3E57E8]"
                />
                <span className="text-[13px] font-poppins text-gray-700">Regular Duty</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="nurseType"
                  value="One Time"
                  checked={carePlan.nurseType === "One Time"}
                  onChange={handleCarePlanChange}
                  className="w-4 h-4 text-[#3E57E8] focus:ring-[#3E57E8]"
                />
                <span className="text-[13px] font-poppins text-gray-700">One Time Service</span>
              </label>
            </div>
          </div>

          {carePlan.nurseType === "Regular" && (
            <>
              <div className="md:col-span-2">
                <label className="block text-[12px] font-medium text-gray-700 mb-1 font-poppins">
                  Duty Shifts
                </label>
                <select
                  name="nurseRegularDutyKey"
                  value={carePlan.nurseRegularDutyKey}
                  onChange={handleCarePlanChange}
                  className={inputClass}
                  disabled={pricingLoading}
                >
                  <option value="" disabled hidden>
                    {pricingLoading
                      ? "Loading..."
                      : nurseRegularDutyOptions.length
                        ? "Select Duty Shift"
                        : "No priced regular duties"}
                  </option>
                  {nurseRegularDutyOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-[12px] font-medium text-gray-700 mb-1 font-poppins">
                  Services
                </label>
                <CreatableSelect
                  isMulti
                  styles={reactSelectStyles}
                  options={nurseRegularServiceOptions}
                  value={nurseRegularServiceOptions.filter((o) =>
                    (carePlan.nurseRegularServices || []).includes(o.value)
                  )}
                  onChange={handleNurseRegularServicesMulti}
                  onCreateOption={handleCreateNurseRegularService}
                  isDisabled={!carePlan.nurseSpecialist}
                  formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
                  placeholder={
                    !carePlan.nurseSpecialist
                      ? "Select specialist first"
                      : "Select services or type to add..."
                  }
                />
              </div>
            </>
          )}

          {carePlan.nurseType === "One Time" && (
            <div className="md:col-span-2">
              <label className="block text-[12px] font-medium text-gray-700 mb-1 font-poppins">
                Services
              </label>
              <Select
                isMulti
                styles={reactSelectStyles}
                options={nurseOneTimeServiceOptions}
                formatOptionLabel={formatServiceOptionWithNotes}
                value={nurseOneTimeServiceOptions.filter((o) =>
                  (carePlan.nurseOneTimeServices || []).includes(o.value)
                )}
                onChange={handleNurseOneTimeServicesMulti}
                isDisabled={pricingLoading || nurseOneTimeServiceOptions.length === 0}
                placeholder={
                  pricingLoading
                    ? "Loading..."
                    : nurseOneTimeServiceOptions.length
                      ? "Select one-time services..."
                      : "No priced one-time services"
                }
              />
            </div>
          )}

          {carePlan.nurseType && (
            <div className="md:col-span-2">
              <label className="block text-[12px] font-medium text-gray-700 mb-1 font-poppins">How many days?</label>
              <input
                type="number"
                name="nurseDays"
                value={carePlan.nurseDays}
                onChange={handleCarePlanChange}
                className={inputClass}
                placeholder="Enter number of days"
                min={1}
                max={99}
              />
            </div>
          )}
        </>
      )}

      {carePlan.serviceCategory ? (
        <div className="md:col-span-2">
          <label className="block text-[12px] font-medium text-gray-700 mb-1 font-poppins">
            Instruction for the patient
          </label>
          <textarea
            name="patientInstructions"
            value={carePlan.patientInstructions}
            onChange={handleCarePlanChange}
            rows={3}
            placeholder="Enter instructions for the patient"
            className={`${inputClass} resize-none`}
          />
        </div>
      ) : null}
    </div>
  );

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6 bg-black/40 backdrop-blur-sm transition-opacity duration-300"
      onClick={() => {
        if (isAddMode) handleRequestClose();
      }}
    >
      <div
        className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl h-[min(760px,92vh)] flex flex-col overflow-hidden transform scale-100 transition-transform duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100">
          <h2 className="text-[18px] font-semibold text-gray-800 font-poppins">{modalTitle}</h2>
          <button
            onClick={handleRequestClose}
            className="p-2 bg-gray-50 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
          >
            <MdClose size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto p-5 md:p-8 custom-scrollbar relative">
          {!isEditMode && renderStepIndicator()}

          {(isEditMode || (!isPrescribeMode && currentStep === 1)) && renderStep1()}
          {!isEditMode && currentStep === 2 && renderStep2()}
          {!isEditMode && currentStep === 3 && renderStep3()}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-100 flex justify-end gap-3 bg-gray-50/50">
          {isEditMode ? (
            <>
              <button
                onClick={handleRequestClose}
                className="px-6 py-2.5 text-[13px] font-medium text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors font-poppins"
              >
                Cancel
              </button>
              <button
                onClick={handleEditUpdate}
                className="px-8 py-2.5 text-[13px] font-medium text-white bg-[#3E57E8] rounded-xl hover:bg-blue-700 shadow-md shadow-blue-500/20 transition-colors font-poppins"
              >
                Update
              </button>
            </>
          ) : (
            <>
              {!isPrescribeMode && currentStep === 1 && (
                <button
                  onClick={handleRequestClose}
                  disabled={isUploadingDischargeSummary || isSubmittingCarePlan}
                  className="px-6 py-2.5 text-[13px] font-medium text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors font-poppins"
                >
                  Cancel
                </button>
              )}

              {currentStep > (isPrescribeMode ? 2 : 1) && (
                <button
                  onClick={() => setCurrentStep((prev) => prev - 1)}
                  disabled={isUploadingDischargeSummary || isSubmittingCarePlan}
                  className="px-6 py-2.5 text-[13px] font-medium text-[#3E57E8] bg-blue-50 rounded-xl hover:bg-blue-100 transition-colors font-poppins"
                >
                  Back
                </button>
              )}

              {currentStep < 3 ? (
                <button
                  onClick={handleNext}
                  disabled={isUploadingDischargeSummary || isCheckingPhone || isSubmittingCarePlan}
                  className="px-8 py-2.5 text-[13px] font-medium text-white bg-[#3E57E8] rounded-xl hover:bg-blue-700 shadow-md shadow-blue-500/20 transition-colors font-poppins"
                >
                  {currentStep === 1 && isCheckingPhone ? (
                    <span className="inline-flex items-center gap-2">
                      <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                      Checking...
                    </span>
                  ) : currentStep === 2 && isUploadingDischargeSummary ? (
                    <span className="inline-flex items-center gap-2">
                      <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                      {isAddMode && !getDischargeUploadState().hasCompleteNewUpload
                        ? "Saving..."
                        : "Uploading..."}
                    </span>
                  ) : currentStep === 2 && isAddMode && !getDischargeUploadState().hasAnyUploadInput ? (
                    "Skip"
                  ) : (
                    "Next"
                  )}
                </button>
              ) : (
                <>
                  {isAddMode && currentStep === 3 && (
                    <button
                      onClick={handleSkipCarePlan}
                      disabled={isUploadingDischargeSummary || isSubmittingCarePlan}
                      className="px-6 py-2.5 text-[13px] font-medium text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors font-poppins"
                    >
                      Skip Care Plan
                    </button>
                  )}
                  <button
                    onClick={handleSubmit}
                    disabled={isUploadingDischargeSummary || isSubmittingCarePlan}
                    className="px-8 py-2.5 text-[13px] font-medium text-white bg-[#3E57E8] rounded-xl hover:bg-blue-700 shadow-md shadow-blue-500/20 transition-colors font-poppins"
                  >
                    {isSubmittingCarePlan ? (
                      <span className="inline-flex items-center gap-2">
                        <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                        Saving...
                      </span>
                    ) : isAddMode && isUploadingDischargeSummary ? (
                      <span className="inline-flex items-center gap-2">
                        <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                        Saving...
                      </span>
                    ) : (
                      "Submit"
                    )}
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

